// @givmo/app-charity — the §321 statement workbook download (CPA-WEB11 → CPA-BE15).
//
// ★ WHY THIS ISN'T AN SDK CALL. `…/section321/statement.xlsx` is a real, merged backend route, but it
// is NOT in `packages/api-client/openapi.json` — the committed snapshot tracks the backend's landed
// spec at the last regen, and the most recent one (#421) absorbed the statement JSON read without the
// workbook route beside it. So there is no generated op to call.
//
// Per `agent_docs/api-client.md` → "A whole ROUTE the snapshot lacks", the sanctioned answer is
// `givmoClient.<method>({ url })` rather than either of the two tempting alternatives: hand-editing
// `src/generated/**` (the drift gate exists to catch exactly that) or refreshing the whole snapshot
// for one endpoint (which absorbs every unrelated backend change since the last regen and collides
// with any regen already in flight). `givmoClient` IS the client the generated ops bind to, so the
// request inherits the entire interceptor stack — Bearer token, App Check, `X-Request-Id`, and
// `GivmoError` mapping — and behaves identically to a generated call in every respect that matters.
//
// This module goes away when a snapshot refresh absorbs the route: replace `requestStatementWorkbook`
// with the generated export op and delete the URL builder. Nothing else changes — the caller already
// speaks in terms of `runBinaryDownload`.

import { givmoClient } from '@givmo/api-client';
import { csvDownloadOptions, runBinaryDownload } from './csv-download';

/** The spreadsheetml media type the route declares. Used only for the defensive non-Blob branch. */
export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** The workbook route for one charity. Mirrors the backend's path exactly. */
export function statementWorkbookUrl(charityId: string): string {
  return `/charity-admin/charities/${encodeURIComponent(charityId)}/section321/statement.xlsx`;
}

/**
 * Fallback filename, used ONLY if the response carries no Content-Disposition. The server names this
 * file `givmo-grants-and-donations-YYYY-MM-DD.xlsx` and its name wins whenever it is present; this
 * mirrors the shape so a fallback download is still recognizable rather than "download.xlsx".
 */
export function fallbackWorkbookName(): string {
  return `givmo-grants-and-donations-${new Date().toISOString().slice(0, 10)}.xlsx`;
}

/**
 * Download the whole-history workbook. Note what the screen's own state does NOT do to it: the
 * spreadsheet is always complete — every donation, whatever the recent-N window is showing and
 * whatever the search box says. An export that silently inherited the screen's filter would be the
 * worst possible artifact to hand an auditor, so no toolbar state is passed here at all.
 */
export async function downloadStatementWorkbook(charityId: string): Promise<void> {
  await runBinaryDownload(
    (options) =>
      givmoClient.get({
        url: statementWorkbookUrl(charityId),
        ...(options as typeof csvDownloadOptions),
      }),
    fallbackWorkbookName(),
    XLSX_MIME,
  );
}
